import { useState, useCallback, useEffect } from "react";
import { Customer } from "@/types/customer";
import { PaymentHistory } from "@/types/payment";
import { realtimeService, RealtimeEvent } from "@/lib/realtime-service";

const getApiUrl = () => {
  return import.meta.env.VITE_KV_API_URL || 'https://qandil-realtime-api.qandil.workers.dev';
};

const getAuthHeaders = (): Record<string, string> => {
  const sessionToken = localStorage.getItem('auth-token');
  const apiToken = import.meta.env.VITE_KV_TOKEN;
  const token = sessionToken || apiToken;

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  if (apiToken) {
    headers['X-API-Token'] = apiToken;
  }

  return headers;
};

export function useCustomerPayments(customer: Customer | null, open: boolean) {
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);

  const fetchPaymentHistory = useCallback(async () => {
    if (!customer) return;

    setIsLoadingPayments(true);
    try {
      const response = await fetch(`${getApiUrl()}/payments/customer/${customer.id}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  }, [customer]);

  const addPaymentToDatabase = useCallback(async (payment: PaymentHistory): Promise<PaymentHistory | null> => {
    try {
      const response = await fetch(`${getApiUrl()}/payments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payment),
      });

      if (!response.ok) {
        throw new Error('Failed to save payment');
      }

      const result = await response.json();
      return {
        ...payment,
        invoiceNumber: result.invoiceNumber || payment.invoiceNumber
      };
    } catch (error) {
      console.error('Error saving payment:', error);
      return null;
    }
  }, []);

  const addBatchPaymentsToDatabase = useCallback(async (payments: PaymentHistory[]): Promise<PaymentHistory[]> => {
    try {
      const response = await fetch(`${getApiUrl()}/payments/batch`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ payments }),
      });

      if (!response.ok) {
        throw new Error('Failed to save batch payments');
      }

      const result = await response.json();
      return result.payments || [];
    } catch (error) {
      console.error('Error saving batch payments:', error);
      return [];
    }
  }, []);

  const deletePaymentFromDatabase = useCallback(async (paymentId: string) => {
    try {
      const response = await fetch(`${getApiUrl()}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to delete payment');
      }

      return true;
    } catch (error) {
      console.error('Error deleting payment:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    if (customer && open) {
      fetchPaymentHistory();
    }
  }, [customer, open, fetchPaymentHistory]);

  useEffect(() => {
    if (!customer || !open) return;

    const handleRealtimeEvent = (event: RealtimeEvent) => {
      if (event.type === 'payment_added') {
        if (event.data.customerId === customer.id) {
          setPaymentHistory(prev => {
            if (prev.some(p => p.id === event.data.id)) return prev;
            return [event.data, ...prev];
          });
        }
      } else if (event.type === 'payment_updated') {
        if (event.data.customerId === customer.id) {
          setPaymentHistory(prev => prev.map(p => p.id === event.data.id ? event.data : p));
        }
      } else if (event.type === 'payment_deleted') {
        setPaymentHistory(prev => prev.filter(p => p.id !== event.data.id));
      }
    };

    const unsubscribe = realtimeService.subscribe(handleRealtimeEvent);
    return () => unsubscribe();
  }, [customer, open]);

  return {
    paymentHistory,
    setPaymentHistory,
    isLoadingPayments,
    fetchPaymentHistory,
    addPaymentToDatabase,
    addBatchPaymentsToDatabase,
    deletePaymentFromDatabase,
  };
}
